import React from 'react'

import {
  Alert,
  AlertProps,
  Box,
  Text,
  TogglerCheckbox,
  WizardStep,
  WizardStepRenderParams,
} from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { Fields } from 'modules/employees/components/UnifiedAddEmployeeModal/Fields/Fields'
import { WizardData } from 'modules/employees/components/UnifiedAddEmployeeModal/types'
import { groupFieldsByGroup } from 'modules/employees/components/UnifiedAddEmployeeModal/utils'
import WorkflowSelector from 'modules/processes/components/WorkflowSelector'

type ExtendedWizardStepRenderParams = WizardStepRenderParams<WizardData> & {
  canAssignPermissionGroup: boolean
  canInviteUserToFactorial: boolean
}

const isCompletedStep = (newData: WizardData) => {
  const validOnboardingProcess = !newData.enableEmployeeOnboarding || !!newData.onboardingProcessId
  return validOnboardingProcess
}

const OnboardingInfoStep = ({
  data,
  onDataChanged,
  canAssignPermissionGroup,
  canInviteUserToFactorial,
}: ExtendedWizardStepRenderParams) => {
  const fields = data.fieldsSchema?.filter((field) => field.category === 'onboarding_info')
  const filterFields = fields?.filter(
    (field) => field.fieldName !== 'permissions_group_id' || canAssignPermissionGroup
  )
  const groupedFields = groupFieldsByGroup(filterFields)
  const [state, setState] = React.useState({
    startDateOpen: false,
    onboardingTemplateSelectorOpen: false,
    clickedOutsideTooltip: false,
  })

  const onToggleSendInvitation = (sendInvitation: boolean | undefined) => {
    onDataChanged({ ...data, sendInvitation })
  }

  const onOnboardingWorkflowSelect = (onboardingProcessId: string | undefined) => {
    onDataChanged({ ...data, onboardingProcessId })
  }

  const getOnboardingTemplateAlertColor = (): AlertProps['color'] => {
    if (state.onboardingTemplateSelectorOpen) return 'info'
    if (!data?.onboardingProcessId) return 'warning'
    return 'note'
  }

  const updateWorkFlowSettings = (
    enableEmployeeOnboarding: boolean,
    onboardingProcessId: string | undefined
  ) => {
    onDataChanged({ ...data, enableEmployeeOnboarding, onboardingProcessId })
  }

  const getOnboardingTemplateAlertText = (): React.ReactNode => {
    if (!data?.onboardingProcessId)
      return i18n.t(
        'employee_form.wizard.onboarding_step.onboarding_template_select.alert.unselected'
      )

    return `✉️ ${i18n.t(
      'employee_form.wizard.onboarding_step.onboarding_template_select.alert.selected'
    )}`
  }

  const handleToggle = (isOpen: boolean) => {
    setState({ ...state, onboardingTemplateSelectorOpen: isOpen })
  }

  const onChangeField = (fieldName: string, value: string) => {
    onDataChanged({
      ...data,
      [fieldName]: value,
    })
  }

  return (
    <Box flexDirection="row" gap="s48">
      <Box flexDirection="column" gap="s24" width="s400">
        <Text.Header2>
          {i18n.t('add_employee_modal.onboarding_info_step.header', { firstName: data?.firstName })}
        </Text.Header2>
        {Object.entries(groupedFields ?? {}).map(([group, groupFields]) => (
          <Box key={group} flexDirection="row" gap="s16">
            {groupFields.map((field) => {
              return (
                <Fields
                  key={field.fieldName}
                  field={field}
                  data={data}
                  onChangeField={onChangeField}
                />
              )
            })}
          </Box>
        ))}
        {canInviteUserToFactorial && (
          <>
            <Box
              border={{ all: { width: 's1', color: 'grey400', style: 'solid' } }}
              padding="s16"
              gap="s16"
              borderRadius={{ all: 'abs008' }}
              flexDirection="row"
              alignItems="flexStart"
              justifyContent="spaceBetween"
            >
              <Box gap="s8">
                <Text.Normal weight="medium" color="grey1000">
                  {i18n.t('add_employee_modal.onboarding_info_step.fields.send_invitation.label')}
                </Text.Normal>
                <Text.Normal size="200">
                  {i18n.t(
                    'add_employee_modal.onboarding_info_step.fields.send_invitation.description'
                  )}
                </Text.Normal>
              </Box>

              <TogglerCheckbox
                value={data?.sendInvitation ?? false}
                onChange={onToggleSendInvitation}
              />
            </Box>
            {data?.sendInvitation && (
              <WorkflowSelector
                enableEmployeeOnboarding={!!data.enableEmployeeOnboarding}
                updateWorkFlowSettings={updateWorkFlowSettings}
                selectedWorkflow={data?.onboardingProcessId}
                onWorkflowSelect={onOnboardingWorkflowSelect}
                mandatory={data.enableEmployeeOnboarding}
                onToggle={handleToggle}
                canAssignWorkflow={canInviteUserToFactorial}
              />
            )}
          </>
        )}
      </Box>
      <Box flexDirection="column" paddingTop="s80" maxWidth="s240">
        {canInviteUserToFactorial && (
          <>
            <Box paddingTop="s80">
              <Alert color="note">
                {i18n.t('employee_form.wizard.contract_step.contract_alert')}
              </Alert>
            </Box>
            {data?.sendInvitation && (
              <Box flexDirection="column" maxWidth="s240">
                <Box paddingTop="s48" height="s140">
                  {!data?.enableEmployeeOnboarding && (
                    <Alert color="note">
                      {i18n.t('employee_form.wizard.onboarding_step.onboarding_card_toggle.alert')}
                    </Alert>
                  )}
                </Box>
                <Box paddingTop="s8" height="s140">
                  {!!data?.enableEmployeeOnboarding && (
                    <Alert color={getOnboardingTemplateAlertColor()}>
                      {getOnboardingTemplateAlertText()}
                    </Alert>
                  )}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}

export const onboardingInfoStep = (
  canAssignPermissionGroup: boolean,
  canInviteUserToFactorial: boolean
): WizardStep<WizardData> => ({
  title: i18n.t('add_employee_modal.onboarding_info_step.title'),
  nextText: i18n.t('add_employee_modal.submit_button'),
  render: (props) => (
    <OnboardingInfoStep
      {...props}
      canAssignPermissionGroup={canAssignPermissionGroup}
      canInviteUserToFactorial={canInviteUserToFactorial}
    />
  ),
  isCompleted: isCompletedStep,
})
